'use client';

import { V2Layout, RevealSection, useV2Lang, Rule, HAIRLINE, ACCENT, PageHeader, Eyebrow, DisplayTitle } from '../_layout';

const COPY = {
  CN: {
    kicker: '支持 PathIN',
    title: '让一首歌\n、一首诗\n走得更远。',
    lede: 'PathIN e.V. 是德国注册的公益机构，所有捐赠用于巡演执行、翻译稿酬、出版和场地——不投放广告，不发工资。每一笔捐赠都可开具德国税务认可的捐赠收据。',
    why: { kicker: '为什么捐赠', items: [
      { k: '01', title: '直接资助艺术家', body: '我们的预算大头是给音乐人和译者的稿酬、差旅与版税。' },
      { k: '02', title: '可抵税', body: '已被德国税务部门认定为公益 (gemeinnützig)，单笔超过 300 € 我们出具正式 Spendenbescheinigung。' },
      { k: '03', title: '可追踪', body: '每个项目结束后我们会公开收支与产出，捐赠人优先收到。' },
    ]},
    bank: { kicker: '银行转账', label: 'Vereinsbank', accountTitle: '账户名', accountValue: 'PathIN e.V.', ibanLabel: 'IBAN', ibanValue: '——（待补充）', bicLabel: 'BIC', bicValue: '——（待补充）', refLabel: '汇款备注', refValue: '建议写：Spende + 您的姓名 + 邮箱（用于开收据）' },
    other: { kicker: '其他方式', items: [
      '联系 pathin@pathin.eu 商讨长期赞助 / 企业赞助',
      '指定项目（巡演 / 翻译 / 出版）请在备注里写明',
      '小额支持也欢迎——任何金额对小机构都是支撑',
    ]},
    cta: { title: '准备好了吗？', body: '银行信息正在公开整理中。最稳定的方式：先发邮件告诉我们金额和指定方向，我们 24 小时内给您完整账户信息和后续流程。', email: 'pathin@pathin.eu', cta: '写信给 PathIN →' },
  },
  EN: {
    kicker: 'Support PathIN',
    title: 'Help one song\nor one poem\ntravel further.',
    lede: "PathIN e.V. is a registered German nonprofit. Every donation goes to tour execution, translator fees, publishing and venue costs — not advertising, not salaries. Tax-deductible receipts available.",
    why: { kicker: 'Why donate', items: [
      { k: '01', title: 'Directly funds artists', body: 'The bulk of our budget is fees, travel and rights for musicians and translators.' },
      { k: '02', title: 'Tax deductible', body: 'Recognized by the German tax office as gemeinnützig. For donations over €300 we issue an official Spendenbescheinigung.' },
      { k: '03', title: 'Traceable', body: "After every project we publish budget and outputs. Donors get the report first." },
    ]},
    bank: { kicker: 'Bank transfer', label: 'Vereinsbank', accountTitle: 'Account holder', accountValue: 'PathIN e.V.', ibanLabel: 'IBAN', ibanValue: '— (to be provided)', bicLabel: 'BIC', bicValue: '— (to be provided)', refLabel: 'Reference', refValue: 'Suggest: "Spende" + your name + email (for the receipt)' },
    other: { kicker: 'Other ways', items: [
      'Email pathin@pathin.eu to discuss long-term or corporate sponsorship',
      'For project-specific donations (tour / translation / publishing), put it in the reference',
      "Small amounts also welcome — every euro matters to a small organization",
    ]},
    cta: { title: 'Ready?', body: "Public bank details are being finalized. The cleanest path right now: email us with amount and direction, and we'll send full account info and next steps within 24 hours.", email: 'pathin@pathin.eu', cta: 'Email PathIN →' },
  },
  DE: {
    kicker: 'PathIN unterstützen',
    title: 'Damit ein Lied\noder ein Gedicht\nweiter trägt.',
    lede: 'PathIN e.V. ist ein in Deutschland eingetragener gemeinnütziger Verein. Jede Spende fließt in Tour-Durchführung, Übersetzer-Honorare, Publishing und Spielstätten — nicht in Werbung, nicht in Gehälter. Steuerlich abzugsfähige Spendenbescheinigung möglich.',
    why: { kicker: 'Warum spenden', items: [
      { k: '01', title: 'Fördert direkt Künstler:innen', body: 'Der Hauptteil unseres Budgets sind Honorare, Reise und Rechte für Musiker:innen und Übersetzer:innen.' },
      { k: '02', title: 'Steuerlich absetzbar', body: 'Vom Finanzamt als gemeinnützig anerkannt. Für Spenden ab 300 € stellen wir eine offizielle Spendenbescheinigung aus.' },
      { k: '03', title: 'Nachvollziehbar', body: 'Nach jedem Projekt veröffentlichen wir Budget und Ergebnisse. Spender:innen erhalten den Report zuerst.' },
    ]},
    bank: { kicker: 'Banküberweisung', label: 'Vereinsbank', accountTitle: 'Kontoinhaber', accountValue: 'PathIN e.V.', ibanLabel: 'IBAN', ibanValue: '— (wird ergänzt)', bicLabel: 'BIC', bicValue: '— (wird ergänzt)', refLabel: 'Verwendungszweck', refValue: 'Empfohlen: "Spende" + Ihr Name + E-Mail (für die Bescheinigung)' },
    other: { kicker: 'Andere Wege', items: [
      'E-Mail an pathin@pathin.eu für Langzeit- oder Unternehmenssponsoring',
      'Für projektgebundene Spenden (Tour / Übersetzung / Publishing) bitte in den Verwendungszweck',
      'Auch kleine Beträge willkommen — jeder Euro zählt für einen kleinen Verein',
    ]},
    cta: { title: 'Bereit?', body: 'Die öffentliche Bankverbindung wird gerade finalisiert. Sauberster Weg jetzt: Schreiben Sie uns kurz Betrag und Richtung — wir senden innerhalb 24 Stunden vollständige Kontoangaben und die nächsten Schritte.', email: 'pathin@pathin.eu', cta: 'PathIN schreiben →' },
  },
};

export default function V2Donate() {
  const lang = useV2Lang();
  const t = COPY[lang];
  return (
    <V2Layout hideFooterContact>
      <PageHeader kicker={t.kicker} title={<DisplayTitle text={t.title} />} lede={t.lede} />

      <Rule />

      {/* Why */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.why.kicker}</Eyebrow></div>
          <div className="md:col-span-9 grid gap-x-12 md:grid-cols-3 border-t" style={{ borderColor: HAIRLINE }}>
            {t.why.items.map((it) => (
              <div key={it.k} className="border-b md:border-b-0 py-10 md:pr-8" style={{ borderColor: HAIRLINE }}>
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: ACCENT }}>{it.k}</span>
                <h3 className="mt-3 text-[clamp(20px,1.8vw,26px)] tracking-tight font-semibold">{it.title}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-white/80">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Bank */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.bank.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <dl className="border-t font-mono text-[14px]" style={{ borderColor: HAIRLINE }}>
              {[[t.bank.accountTitle, t.bank.accountValue], [t.bank.ibanLabel, t.bank.ibanValue], [t.bank.bicLabel, t.bank.bicValue], [t.bank.refLabel, t.bank.refValue]].map(([k, v]) => (
                <div key={k} className="grid grid-cols-12 gap-4 py-5 border-b" style={{ borderColor: HAIRLINE }}>
                  <dt className="col-span-12 md:col-span-4 text-white/65 uppercase tracking-wider text-[11px]">{k}</dt>
                  <dd className="col-span-12 md:col-span-8 text-white">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Other ways */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.other.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <ul className="border-t" style={{ borderColor: HAIRLINE }}>
              {t.other.items.map((it, i) => (
                <li key={i} className="border-b py-5 text-[16px] text-white/80 flex gap-4" style={{ borderColor: HAIRLINE }}>
                  <span className="font-mono text-[11px] uppercase tracking-wider text-white/55 shrink-0 mt-1.5">0{i+1}</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* CTA */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow accent>→</Eyebrow></div>
          <div className="md:col-span-9">
            <h2 className="text-[clamp(32px,4vw,60px)] leading-[1.05] tracking-[-0.025em] font-bold">{t.cta.title}</h2>
            <p className="mt-6 text-[16px] leading-[1.65] text-white/70 max-w-[60ch]">{t.cta.body}</p>
            <a href={`mailto:${t.cta.email}`} className="inline-block mt-8 px-6 py-3 border text-[14px] tracking-wide hover:bg-white hover:text-black transition-colors" style={{ borderColor: 'rgba(255,255,255,0.4)' }}>
              {t.cta.cta}
            </a>
          </div>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}
